import { useEffect, useRef, useState, useCallback } from "react";
import { Send, Loader2, MessageCircle, Sparkles, CheckCheck, Paperclip, Mic, Square, Download, Play, Bell, ArrowDownToLine } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "stilocred_chat_session";
const ROYAL = "#1d4ed8";

const urlAbs = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

async function fetchJson(url, options) {
  const response = await fetch(url, options);
  let data = {};
  try { data = await response.json(); } catch (_) {}
  if (!response.ok) throw new Error(data?.detail || `Falha de comunicação (${response.status})`);
  return data;
}

function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function readUrlParams() {
  const p = new URLSearchParams(window.location.search);
  const out = {};
  ["origem", "cupom", "nome", "fluxo", "utm_source", "utm_campaign", "ref"].forEach((k) => {
    const v = p.get(k);
    if (v) out[k] = v;
  });
  return out;
}

function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const MediaContent = ({ m }) => {
  const t = m.tipo || m.media_tipo;
  const src = urlAbs(m.media_url);
  if (t === "imagem" || t === "image") return <img src={src} alt="" className="mb-1 rounded-lg max-w-full max-h-72 object-cover" />;
  if (t === "video") return <video src={src} controls className="mb-1 rounded-lg max-w-full max-h-72" />;
  if (t === "audio") return <audio src={src} controls className="mb-1 w-56 max-w-full" />;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="mb-1 flex items-center gap-2 underline">
      <Download className="h-4 w-4" /> {m.text || "Baixar documento"}
    </a>
  );
};

const Bubble = ({ m }) => {
  const isUser = m.from === "user";
  return (
    <div data-testid={`chat-msg-${m.from}`} className={`flex w-full ${isUser ? "justify-end" : "justify-start"} animate-[fadeup_.25s_ease]`}>
      <div className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap break-words shadow-sm ${
        isUser ? "bg-blue-600 text-white rounded-br-md" : "bg-slate-800/80 text-slate-100 rounded-bl-md border border-white/5"}`}>
        {m.media_url && <MediaContent m={m} />}
        {m.text}
      </div>
    </div>
  );
};

export default function ChatMobile() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [awaiting, setAwaiting] = useState("input");
  const [buttons, setButtons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const scrollRef = useRef(null);
  const lastTsRef = useRef(null);
  const sidRef = useRef(getSessionId());
  const fileRef = useRef(null);
  const mediaRec = useRef(null);
  const chunks = useRef([]);
  const deferredPrompt = useRef(null);
  const pushAsked = useRef(false);

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }));
  }, []);

  const applyState = useCallback((data) => {
    if (!data) return;
    setAwaiting(data.awaiting || "input");
    setButtons(data.buttons || []);
  }, []);

  const appendMessages = useCallback((newMsgs) => {
    if (!newMsgs || !newMsgs.length) return;
    setMessages((prev) => {
      const merged = [...prev];
      const byId = new Set(merged.map((x) => x.id));
      newMsgs.forEach((m) => {
        if (!m || byId.has(m.id)) return;
        if (m.from === "user") {
          const idx = merged.findIndex((x) => String(x.id).startsWith("local-") && x.from === "user" &&
            ((m.media_url && x.media_url === m.media_url) || (!m.media_url && x.text === m.text && !x.media_url)));
          if (idx !== -1) { merged[idx] = m; byId.add(m.id); return; }
        }
        merged.push(m); byId.add(m.id);
      });
      return merged;
    });
    const withTs = newMsgs.filter((m) => m && m.created_at);
    if (withTs.length) {
      const maxTs = withTs.map((m) => m.created_at).sort().slice(-1)[0];
      if (!lastTsRef.current || maxTs > lastTsRef.current) lastTsRef.current = maxTs;
    }
  }, []);

  const ensurePush = useCallback(async () => {
    if (pushAsked.current) return;
    pushAsked.current = true;
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      if (Notification.permission === "denied") return;
      const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
      if (perm !== "granted") return;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const { public_key, enabled } = await (await fetch(`${API}/chat/push/pubkey`)).json();
        if (!enabled || !public_key) return;
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(public_key) });
      }
      await fetch(`${API}/chat/push/subscribe`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sidRef.current, subscription: sub }) });
    } catch (e) { /* silent */ }
  }, []);

  // PWA setup
  useEffect(() => {
    document.title = "StiloCred — Atendimento";
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) { link = document.createElement("link"); link.rel = "manifest"; document.head.appendChild(link); }
    link.href = "/chat-manifest.json";
    let theme = document.querySelector('meta[name="theme-color"]');
    if (!theme) { theme = document.createElement("meta"); theme.name = "theme-color"; document.head.appendChild(theme); }
    theme.setAttribute("content", "#0b1220");
    const prevBg = document.body.style.background, prevOverflow = document.body.style.overflow;
    document.body.style.background = "#0b1220";
    document.body.style.overflow = "hidden";
    const hideBadge = document.createElement("style");
    hideBadge.id = "chat-hide-badge";
    hideBadge.textContent = "#emergent-badge{display:none !important;}";
    document.head.appendChild(hideBadge);
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/chat-sw.js", { scope: "/chat" }).catch(() => {});
    const onBip = (e) => { e.preventDefault(); deferredPrompt.current = e; setCanInstall(true); };
    window.addEventListener("beforeinstallprompt", onBip);
    return () => {
      document.body.style.background = prevBg; document.body.style.overflow = prevOverflow;
      document.getElementById("chat-hide-badge")?.remove();
      window.removeEventListener("beforeinstallprompt", onBip);
    };
  }, []);

  // Start / resume session
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJson(`${API}/chat/session`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sidRef.current, params: readUrlParams() }) });
        setMessages(data.messages || []);
        applyState(data);
        const arr = (data.messages || []).filter((m) => m.created_at);
        if (arr.length) lastTsRef.current = arr[arr.length - 1].created_at;
      } catch (e) {
        setMessages([{ id: "err", from: "bot", text: "Não foi possível conectar. Tente novamente em instantes." }]);
      } finally { setLoading(false); scrollToEnd(); }
    })();
  }, [applyState, scrollToEnd]);

  useEffect(() => { scrollToEnd(); }, [messages, scrollToEnd]);

  // Continuous polling
  useEffect(() => {
    if (loading) return;
    const iv = setInterval(async () => {
      try {
        const url = `${API}/chat/history?session_id=${encodeURIComponent(sidRef.current)}${lastTsRef.current ? `&after=${encodeURIComponent(lastTsRef.current)}` : ""}`;
        const data = await fetchJson(url);
        appendMessages(data.messages);
        applyState(data);
      } catch (e) { /* silent */ }
    }, 2500);
    return () => clearInterval(iv);
  }, [loading, appendMessages, applyState]);

  const send = useCallback(async (text) => {
    const value = (text ?? input).trim();
    if (!value || sending) return;
    setInput(""); setSending(true); setButtons([]);
    setMessages((prev) => [...prev, { id: `local-${Date.now()}`, from: "user", text: value }]);
    scrollToEnd();
    ensurePush();
    try {
      const data = await fetchJson(`${API}/chat/message`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sidRef.current, text: value }) });
      appendMessages([data.user_msg, ...(data.messages || [])].filter(Boolean));
      applyState(data);
    } catch (e) {
      appendMessages([{ id: `err-${Date.now()}`, from: "bot", text: "Ops, houve um erro. Pode repetir?" }]);
    } finally { setSending(false); scrollToEnd(); }
  }, [input, sending, appendMessages, applyState, scrollToEnd, ensurePush]);

  const uploadAndSend = useCallback(async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    const optimisticId = `local-${Date.now()}`;
    ensurePush();
    try {
      const fd = new FormData(); fd.append("file", file);
      const { url, tipo } = await fetchJson(`${API}/wpp/chat/upload`, { method: "POST", body: fd });
      setMessages((prev) => [...prev, { id: optimisticId, from: "user", text: "", tipo, media_url: url }]);
      scrollToEnd();
      const data = await fetchJson(`${API}/chat/message`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sidRef.current, media_url: url, media_tipo: tipo }) });
      appendMessages([data.user_msg, ...(data.messages || [])].filter(Boolean));
      applyState(data);
    } catch (e) {
      appendMessages([{ id: `err-${Date.now()}`, from: "bot", text: "Não consegui enviar o arquivo. Tente novamente." }]);
    } finally { setUploading(false); scrollToEnd(); }
  }, [uploading, appendMessages, applyState, scrollToEnd, ensurePush]);

  const onPickFile = (e) => { const f = e.target.files?.[0]; if (f) uploadAndSend(f); e.target.value = ""; };

  const toggleRecord = useCallback(async () => {
    if (recording) { mediaRec.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        if (blob.size > 500) await uploadAndSend(new File([blob], "audio.webm", { type: "audio/webm" }));
      };
      mediaRec.current = mr; mr.start(); setRecording(true);
    } catch (e) { setRecording(false); }
  }, [recording, uploadAndSend]);

  const install = async () => { const e = deferredPrompt.current; if (!e) return; e.prompt(); await e.userChoice; deferredPrompt.current = null; setCanInstall(false); };

  return (
    <div data-testid="chat-mobile-root" className="fixed inset-0 flex flex-col overflow-hidden text-slate-100" style={{ height: "100dvh", background: "radial-gradient(1200px 600px at 50% -10%, #16233f 0%, #0b1220 60%)" }}>
      <style>{`@keyframes fadeup{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}@keyframes blink{0%,80%,100%{opacity:.25}40%{opacity:1}}@keyframes pulsered{0%,100%{opacity:1}50%{opacity:.4}}`}</style>

      <header className="flex items-center gap-3 px-4 py-3 border-b border-white/10 backdrop-blur-md bg-white/5">
        <div className="relative">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: ROYAL }}><MessageCircle className="h-5 w-5 text-white" /></div>
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 border-2 border-[#0b1220]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[15px] leading-tight flex items-center gap-1">StiloCred <Sparkles className="h-3.5 w-3.5 text-blue-300" /></div>
          <div className="text-xs text-emerald-300/90">online agora</div>
        </div>
        {canInstall && (
          <button data-testid="chat-install-button" onClick={install} className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-blue-600/20 text-blue-200 border border-blue-500/30 active:scale-95 transition-all">
            <ArrowDownToLine className="h-3.5 w-3.5" /> Instalar
          </button>
        )}
      </header>

      <main ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {loading && <div className="flex justify-center pt-10"><Loader2 className="h-6 w-6 animate-spin text-blue-300" /></div>}
        {messages.map((m) => <Bubble key={m.id} m={m} />)}
        {(sending || uploading) && (
          <div className="flex justify-start">
            <div className="bg-slate-800/80 border border-white/5 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
              <span className="h-2 w-2 rounded-full bg-slate-300" style={{ animation: "blink 1.2s infinite 0s" }} />
              <span className="h-2 w-2 rounded-full bg-slate-300" style={{ animation: "blink 1.2s infinite .2s" }} />
              <span className="h-2 w-2 rounded-full bg-slate-300" style={{ animation: "blink 1.2s infinite .4s" }} />
            </div>
          </div>
        )}
        {awaiting === "human" && !sending && !uploading && (
          <div className="flex justify-center">
            <div className="text-xs text-slate-400 bg-white/5 rounded-full px-3 py-1 flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5" /> Um atendente irá continuar seu atendimento</div>
          </div>
        )}
      </main>

      {awaiting === "buttons" && buttons.length > 0 && (
        <div data-testid="chat-quick-replies" className="px-4 pb-2 flex flex-wrap gap-2">
          {buttons.map((b, i) => (
            <button key={i} data-testid={`chat-quick-reply-${i}`} onClick={() => send(b)} disabled={sending}
              className="px-4 py-2 rounded-full text-sm font-medium bg-blue-600/15 text-blue-200 border border-blue-500/30 hover:bg-blue-600/30 active:scale-95 transition-all">{b}</button>
          ))}
        </div>
      )}

      <footer className="px-3 pb-3 pt-2 border-t border-white/10 bg-[#0b1220]/95 backdrop-blur-md relative z-50" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 3.25rem)" }}>
        <input ref={fileRef} type="file" accept="image/*,video/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx" className="hidden" data-testid="chat-file-input" onChange={onPickFile} />
        <form data-testid="chat-form" onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-1.5">
          <button type="button" data-testid="chat-attach-button" onClick={() => fileRef.current?.click()} disabled={uploading || recording}
            className="h-11 w-11 shrink-0 rounded-2xl flex items-center justify-center text-slate-300 bg-slate-800/60 border border-white/10 active:scale-95 transition-all disabled:opacity-40">
            <Paperclip className="h-5 w-5" />
          </button>
          <textarea data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1}
            placeholder={recording ? "Gravando áudio..." : "Digite sua mensagem..."}
            disabled={recording}
            className="flex-1 resize-none max-h-32 rounded-2xl bg-slate-800/70 border border-white/10 px-4 py-3 text-[15px] text-slate-100 placeholder:text-slate-500 outline-none focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-70" />
          {input.trim() ? (
            <button type="submit" data-testid="chat-send-button" disabled={sending || !input.trim()}
              className="h-11 w-11 shrink-0 rounded-2xl flex items-center justify-center text-white shadow-lg disabled:opacity-40 active:scale-95 transition-all" style={{ background: ROYAL }}>
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </button>
          ) : (
            <button type="button" data-testid="chat-mic-button" onClick={toggleRecord} disabled={uploading}
              className={`h-11 w-11 shrink-0 rounded-2xl flex items-center justify-center text-white shadow-lg active:scale-95 transition-all disabled:opacity-40 ${recording ? "bg-red-600" : ""}`}
              style={recording ? { animation: "pulsered 1s infinite" } : { background: ROYAL }}>
              {recording ? <Square className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
          )}
        </form>
      </footer>
    </div>
  );
}
